import React, { useState, useEffect, useCallback } from 'react';

function Dashboard({ user, onLogout }) {
  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [version, setVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(null);
  const [openVersions, setOpenVersions] = useState({});
  const [licenses, setLicenses] = useState([]);

  const loadFiles = useCallback(async () => {
    try {
      const res = await fetch('/api/uploads', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setFiles(data);
      }
    } catch {
      // ignore
    }
  }, []);

  const loadLicenses = useCallback(async () => {
    try {
      const res = await fetch('/api/licenses', { credentials: 'include' });
      if (res.ok) setLicenses(await res.json());
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => { loadFiles(); loadLicenses(); }, [loadFiles, loadLicenses]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0) return;
    if (!version.trim()) {
      setMessage({ type: 'error', text: 'La versione è obbligatoria' });
      return;
    }

    setMessage(null);

    try {
      const check = await fetch('/api/validate-version', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: version.trim() }),
      });
      if (!check.ok) {
        const err = await check.json();
        setMessage({ type: 'error', text: err.error || 'Versione non valida' });
        return;
      }
    } catch {
      setMessage({ type: 'error', text: 'Errore di connessione' });
      return;
    }

    setUploading(true);
    setProgress(0);

    const form = e.target;
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
    const allFiles = Array.from(selectedFiles);
    const totalSize = allFiles.reduce((sum, f) => sum + f.size, 0);
    let uploadedSize = 0;

    try {
      // Inizializza la sessione di upload
      const initRes = await fetch('/api/upload-init', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: version.trim(), fileCount: allFiles.length }),
      });
      if (!initRes.ok) {
        const err = await initRes.json();
        throw new Error(err.error || 'Errore inizializzazione upload');
      }
      const { uploadId } = await initRes.json();

      // Invia ogni file a chunk
      for (let fi = 0; fi < allFiles.length; fi++) {
        const file = allFiles[fi];
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);

          const chunkForm = new FormData();
          chunkForm.append('chunk', chunk);
          chunkForm.append('uploadId', uploadId);
          chunkForm.append('fileName', file.name);
          chunkForm.append('chunkIndex', chunkIndex);
          chunkForm.append('totalChunks', totalChunks);

          const chunkRes = await fetch('/api/upload-chunk', {
            method: 'POST',
            credentials: 'include',
            body: chunkForm,
          });
          if (!chunkRes.ok) {
            const err = await chunkRes.json();
            throw new Error(err.error || `Errore upload chunk ${chunkIndex + 1}/${totalChunks}`);
          }

          uploadedSize += (end - start);
          setProgress(Math.round((uploadedSize / totalSize) * 100));
        }
      }

      // Finalizza l'upload
      const completeRes = await fetch('/api/upload-complete', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId }),
      });
      const data = await completeRes.json();
      if (!completeRes.ok) {
        throw new Error(data.error || 'Errore finalizzazione upload');
      }

      setMessage({ type: 'success', text: `${data.files.length} file caricati con successo` });
      setSelectedFiles(null);
      setVersion('');
      form.reset();
      loadFiles();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Errore durante l\'upload' });
    }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo file?')) return;
    try {
      const res = await fetch(`/api/uploads/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) loadFiles();
    } catch {
      // ignore
    }
  };

  const handleDeleteVersion = async (ver) => {
    if (!window.confirm(`Eliminare la versione ${ver} e tutti i file associati?`)) return;
    try {
      const res = await fetch(`/api/uploads/version/${encodeURIComponent(ver)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) loadFiles();
    } catch {
      // ignore
    }
  };

  const handleToggleLicense = async (id, currentActive) => {
    try {
      const res = await fetch(`/api/licenses/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !currentActive }),
      });
      if (res.ok) loadLicenses();
    } catch {
      // ignore
    }
  };

  const handleDeleteLicense = async (id) => {
    if (!window.confirm('Eliminare questa licenza?')) return;
    try {
      const res = await fetch(`/api/licenses/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) loadLicenses();
    } catch {
      // ignore
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'Z').toLocaleString('it-IT');
  };

  const toggleVersion = (ver) => {
    setOpenVersions(prev => ({ ...prev, [ver]: !prev[ver] }));
  };

  const groupedByVersion = files.reduce((acc, file) => {
    const ver = file.version || 'Senza versione';
    if (!acc[ver]) acc[ver] = [];
    acc[ver].push(file);
    return acc;
  }, {});

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>HiPLaaS Update Manager</h1>
        <div className="user-info">
          <span>{user.username}</span>
          <button className="btn btn-danger btn-sm" onClick={onLogout}>Esci</button>
        </div>
      </div>

      <div className="upload-section">
        <div className="upload-section-header">
          <h2>Carica aggiornamento</h2>
          <input
            type="text"
            className="version-input"
            placeholder="Versione (es. 1.2.0) *"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
          />
        </div>
        <form className="upload-form" onSubmit={handleUpload}>
          <div className="upload-row">
            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".exe,.blockmap,.yml,.yaml"
                multiple
                onChange={(e) => setSelectedFiles(e.target.files)}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={uploading || !selectedFiles || !version.trim()}>
              {uploading ? 'Caricamento...' : 'Carica'}
            </button>
          </div>
          <p className="upload-hint">Formati accettati: .exe, .blockmap, .yml — Puoi selezionare piu file contemporaneamente</p>
        </form>
        {uploading && (
          <div className="progress-container" style={{ marginTop: '1rem' }}>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="progress-text">{progress}%</span>
          </div>
        )}
        {message && (
          <div className={message.type === 'success' ? 'success-msg' : 'error-msg'} style={{ marginTop: '1rem' }}>
            {message.text}
          </div>
        )}
      </div>

      <div className="files-section">
        <h2>File caricati</h2>
        {files.length === 0 ? (
          <p className="no-files">Nessun file caricato</p>
        ) : (
          <div className="accordion">
            {Object.entries(groupedByVersion).map(([ver, versionFiles]) => (
              <div className="accordion-item" key={ver}>
                <div className="accordion-header">
                  <button className="accordion-toggle" onClick={() => toggleVersion(ver)}>
                    <div className="accordion-title">
                      <span className={`accordion-arrow ${openVersions[ver] ? 'open' : ''}`}>&#9654;</span>
                      <span>v{ver}</span>
                      <span className="accordion-count">{versionFiles.length} file</span>
                    </div>
                    <span className="accordion-date">{formatDate(versionFiles[0].uploaded_at)}</span>
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDeleteVersion(ver)}>
                    Elimina versione
                  </button>
                </div>
                {openVersions[ver] && (
                  <div className="accordion-body">
                    <table className="files-table">
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Tipo</th>
                          <th>Dimensione</th>
                          <th>Data</th>
                          <th>Utente</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {versionFiles.map(file => (
                          <tr key={file.id}>
                            <td>{file.original_name}</td>
                            <td>
                              <span className={`file-type ${file.file_type === '.exe' ? 'file-type-exe' : 'file-type-yml'}`}>
                                {file.file_type}
                              </span>
                            </td>
                            <td>{formatSize(file.size)}</td>
                            <td>{formatDate(file.uploaded_at)}</td>
                            <td>{file.uploaded_by}</td>
                            <td>
                              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(file.id)}>
                                Elimina
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="files-section">
        <h2>Licenze registrate ({licenses.length})</h2>
        {licenses.length === 0 ? (
          <p className="no-files">Nessuna licenza registrata</p>
        ) : (
          <table className="files-table">
            <thead>
              <tr>
                <th>Machine ID</th>
                <th>Stato</th>
                <th>Registrata</th>
                <th>Ultimo check</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {licenses.map(lic => (
                <tr key={lic.id}>
                  <td style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>{lic.machine_id}</td>
                  <td>
                    <span className={`file-type ${lic.active ? 'file-type-exe' : 'file-type-yml'}`}>
                      {lic.active ? 'Attiva' : 'Revocata'}
                    </span>
                  </td>
                  <td>{formatDate(lic.registered_at)}</td>
                  <td>{formatDate(lic.last_check)}</td>
                  <td style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className={`btn btn-sm ${lic.active ? 'btn-danger' : 'btn-primary'}`}
                      onClick={() => handleToggleLicense(lic.id, lic.active)}
                    >
                      {lic.active ? 'Revoca' : 'Attiva'}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteLicense(lic.id)}>
                      Elimina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="api-section">
        <h2>API per il client</h2>
        <div className="api-endpoint">
          POST /api/register — body: {'{'} "machine_id": "..." {'}'}
        </div>
        <p className="api-description" style={{ marginBottom: '0.75rem' }}>
          Registra la macchina e ottiene la license_key da usare nelle chiamate successive.
        </p>
        <div className="api-endpoint">
          GET /api/check-update?version=X.Y.Z — header: X-License-Key
        </div>
        <p className="api-description">
          Verifica aggiornamenti. Richiede header X-License-Key con la chiave ottenuta dalla registrazione.
        </p>
      </div>
    </div>
  );
}

export default Dashboard;
